import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  MagnifyingGlass, CircleNotch, Upload, FileText, X, CheckCircle,
  WarningCircle, Plus, LinkSimple, ArrowSquareOut,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const NETWORK_COLORS = {
  impact: "bg-slate-100 text-slate-700",
  partnerize: "bg-indigo-50 text-indigo-700",
  skimlinks: "bg-orange-50 text-orange-700",
  mrge: "bg-emerald-50 text-emerald-700",
  cj: "bg-amber-50 text-amber-700",
  pubadhub: "bg-cyan-50 text-cyan-700",
  pubadhub_kevin: "bg-teal-50 text-teal-700",
  admedia: "bg-rose-50 text-rose-700",
  yieldkit: "bg-fuchsia-50 text-fuchsia-700",
  flexoffers: "bg-violet-50 text-violet-700",
  shopnomix_kevin: "bg-pink-50 text-pink-700",
  shopnomix: "bg-lime-50 text-lime-700",
};

function NetworkPill({ network }) {
  const n = (network || "").toLowerCase();
  if (!n) return <span className="text-slate-400 text-xs">—</span>;
  const cls = NETWORK_COLORS[n] || "bg-slate-100 text-slate-700";
  const lbl = n === "pubadhub_kevin" ? "pah-kevin" : n;
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${cls}`}
      data-testid={`deep-search-network-${n}`}
    >
      {lbl}
    </span>
  );
}

// Color-coded match-quality badge so admins see why a result surfaced
// (exact name beats a fuzzy substring hit).
const MATCH_LABEL = {
  exact: { label: "Exact", cls: "bg-emerald-50 text-emerald-700" },
  campaign_id: { label: "ID", cls: "bg-blue-50 text-blue-700" },
  domain: { label: "Domain", cls: "bg-purple-50 text-purple-700" },
  substring: { label: "Fuzzy", cls: "bg-slate-100 text-slate-600" },
};

function TrackingLinkCell({ url }) {
  if (!url) return <span className="text-slate-400 text-xs">—</span>;
  const copy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(url).then(
      () => toast.success("Tracking link copied"),
      () => toast.error("Copy failed"),
    );
  };
  return (
    <a
      href={url}
      onClick={copy}
      className="inline-flex items-center gap-1 text-xs font-medium text-[#002FA7] hover:underline whitespace-nowrap"
      title={url}
      data-testid="deep-search-tracking-link"
    >
      <LinkSimple size={12} weight="bold" />
      Copy link
    </a>
  );
}

function formatGeo(brand) {
  if (!brand) return "—";
  const geo = brand.geo_locations || brand.countries;
  if (Array.isArray(geo) && geo.length) return geo.join(", ");
  if (brand.country) return brand.country;
  if (brand.country_code) return brand.country_code;
  const regions = brand.shipping_regions;
  if (Array.isArray(regions) && regions.length) return regions.join(", ");
  return "—";
}

function displayDomain(brand) {
  if (!brand) return "—";
  if (brand.domain) return brand.domain;
  const url = brand.campaign_url || brand.advertiser_url || "";
  if (!url) return "—";
  try {
    return new URL(url.startsWith("http") ? url : `https://${url}`).hostname.replace(/^www\./i, "");
  } catch {
    return "—";
  }
}

function DetailRow({ label, value, mono = false, copyValue = null }) {
  const copy = () => {
    if (!copyValue) return;
    navigator.clipboard.writeText(copyValue).then(
      () => toast.success("Copied"),
      () => toast.error("Copy failed"),
    );
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className={`mt-0.5 text-sm text-slate-900 break-all ${mono ? "font-mono text-xs" : ""}`}>
        {value ?? "—"}
        {copyValue && (
          <button
            type="button"
            onClick={copy}
            className="ml-2 text-[11px] font-medium text-[#002FA7] hover:underline"
          >
            Copy
          </button>
        )}
      </div>
    </div>
  );
}

function ExternalLink({ href, label }) {
  if (!href) return <span className="text-slate-400">—</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-[#002FA7] hover:underline break-all"
    >
      {label || href.replace(/^https?:\/\//, "")}
      <ArrowSquareOut size={12} />
    </a>
  );
}

function BrandDetailSheet({ match, open, onClose }) {
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !match?.brand_id) {
      setBrand(null);
      return;
    }
    if (String(match.brand_id).startsWith("yk_")) {
      setBrand({
        campaign_id: match.brand_id,
        name: match.brand_name,
        advertiser_name: match.advertiser_name,
        network: match.network,
        domain: match.domain,
        country: match.country,
        campaign_url: match.campaign_url || match.advertiser_url,
        advertiser_url: match.advertiser_url,
        tracking_link: match.tracking_link,
        payout_display: match.rate_display,
        offer_type: match.offer_type,
        contract_status: match.status,
      });
      return;
    }
    setLoading(true);
    API.get("/admin/brands", { params: { q: match.brand_id, limit: 10 } })
      .then(({ data }) => {
        const items = Array.isArray(data) ? data : (data?.items || []);
        const hit = items.find((b) => b.campaign_id === match.brand_id) || items[0];
        if (!hit) throw new Error("Brand not found");
        setBrand(hit);
      })
      .catch((err) => {
        toast.error(err.response?.data?.detail || err.message || "Could not load brand");
        onClose();
      })
      .finally(() => setLoading(false));
  }, [open, match, onClose]);

  const tracking = brand?.tracking_link || match?.tracking_link || "";
  const brandUrl = brand?.campaign_url || match?.campaign_url || "";
  const advertiserUrl = brand?.advertiser_url || match?.advertiser_url || "";

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="w-[520px] sm:max-w-[520px] overflow-y-auto"
        data-testid="deep-search-brand-detail"
      >
        <SheetHeader>
          <SheetTitle>{match?.brand_name || brand?.name || "Brand"}</SheetTitle>
          <SheetDescription className="flex flex-wrap items-center gap-2 pt-1">
            <span className="font-mono text-xs">{match?.brand_id}</span>
            {match?.network && <NetworkPill network={match.network} />}
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <p className="text-sm text-slate-500 mt-8">Loading brand details…</p>
        ) : (
          <div className="mt-6 space-y-4">
            <DetailRow label="Advertiser" value={brand?.advertiser_name || match?.advertiser_name || "—"} />
            <DetailRow label="Domain" value={displayDomain(brand || match)} mono />
            <DetailRow label="Country / GEO" value={formatGeo(brand || match)} />
            <DetailRow
              label="Tracking URL"
              value={tracking ? (
                <ExternalLink href={tracking} label={tracking.replace(/^https?:\/\//, "").slice(0, 60)} />
              ) : "—"}
              copyValue={tracking || null}
            />
            <DetailRow
              label="Brand URL"
              value={<ExternalLink href={brandUrl} />}
            />
            <DetailRow
              label="Advertiser URL"
              value={<ExternalLink href={advertiserUrl} />}
            />
            <div className="grid grid-cols-2 gap-4">
              <DetailRow
                label="Rate"
                value={brand?.payout_display || match?.rate_display || "—"}
                mono
              />
              <DetailRow
                label="Base commission"
                value={
                  brand?.base_commission_pct != null
                    ? `${brand.base_commission_pct}%`
                    : "—"
                }
                mono
              />
              <DetailRow label="Offer type" value={(brand?.offer_type || match?.offer_type || "—").toUpperCase()} />
              <DetailRow label="Status" value={brand?.contract_status || brand?.status || match?.status || "—"} />
            </div>
            {brand?.deeplink != null && (
              <DetailRow
                label="API deeplink"
                value={brand.deeplink || "Not supported"}
                mono
                copyValue={brand.deeplink || null}
              />
            )}
            {String(match?.brand_id || "").startsWith("yk_") && (
              <p className="text-xs text-slate-500 border border-slate-200 rounded-md p-3 bg-slate-50">
                Yieldkit tracking links are publisher-specific. Assign this brand to a publisher to generate a stamped tracker URL.
              </p>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function AdminDeepSearch() {
  const [text, setText] = useState("");
  const [publisherId, setPublisherId] = useState("");
  const [network, setNetwork] = useState("all");
  const [publishers, setPublishers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null); // {results, unmatched, total_matches, ...}
  const [selected, setSelected] = useState(new Set());
  const [assigning, setAssigning] = useState(false);
  const [detailMatch, setDetailMatch] = useState(null);
  const fileInputRef = useRef(null);

  // Lazy-load the publisher list — only the first time the dropdown opens.
  const ensurePublishers = useCallback(async () => {
    if (publishers.length > 0) return;
    try {
      const { data } = await API.get("/admin/publishers?page=1&page_size=500");
      const items = Array.isArray(data) ? data : (data?.publishers || data?.items || []);
      setPublishers(items);
    } catch (err) {
      console.error(err);
    }
  }, [publishers.length]);

  const runSearch = useCallback(async () => {
    if (!text.trim()) {
      toast.error("Paste at least one brand name");
      return;
    }
    setLoading(true);
    setSelected(new Set());
    try {
      const { data } = await API.post("/admin/deep-search", {
        text,
        publisher_id: publisherId || undefined,
        network: network !== "all" ? network : undefined,
      });
      setResults(data);
      const totalMatches = data?.total_matches || 0;
      const unmatched = (data?.unmatched || []).length;
      toast.success(
        `Found ${totalMatches} match${totalMatches === 1 ? "" : "es"}` +
        (unmatched ? ` · ${unmatched} not found` : "")
      );
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
    }
  }, [text, publisherId, network]);

  const uploadFile = useCallback(async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    if (publisherId) form.append("publisher_id", publisherId);
    if (network !== "all") form.append("network", network);
    setLoading(true);
    setSelected(new Set());
    try {
      const { data } = await API.post("/admin/deep-search/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Surface the parsed queries in the textarea so the admin can
      // see what we ingested and re-edit if needed.
      setText((data?.queries || []).join("\n"));
      setResults(data);
      toast.success(`Parsed ${data?.total_queries || 0} queries from file`);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setLoading(false);
    }
  }, [publisherId, network]);

  const toggleSelect = (brandId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(brandId)) next.delete(brandId);
      else next.add(brandId);
      return next;
    });
  };

  // Bulk-assign uses the existing /admin/publisher-brands/bulk-assign
  // endpoint with passthrough + the pub's default split — same path the
  // Smart Add button on the publisher detail page exercises.
  const assignSelected = async () => {
    if (!publisherId) {
      toast.error("Pick a publisher first");
      return;
    }
    if (selected.size === 0) {
      toast.error("Select at least one brand");
      return;
    }
    setAssigning(true);
    try {
      const { data } = await API.post("/admin/publisher-brands/bulk-assign", {
        publisher_id: publisherId,
        brand_ids: Array.from(selected),
        payout_mode: "passthrough",
      });
      toast.success(
        `Assigned ${data.assigned} · skipped ${data.skipped}` +
        (data.skipped > 0 ? " (already-assigned / no rate)" : "")
      );
      // Re-run search to refresh the assigned flags.
      runSearch();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Assign failed");
    } finally {
      setAssigning(false);
    }
  };

  // Flat matches array (across every query group) for "Select all" + counts.
  const flatMatches = useMemo(() => {
    if (!results?.results) return [];
    return results.results.flatMap((g) => g.matches);
  }, [results]);

  const selectableCount = flatMatches.filter((m) => !m.assigned).length;
  const allSelected = selectableCount > 0 && selected.size === selectableCount;

  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(flatMatches.filter((m) => !m.assigned).map((m) => m.brand_id)));
  };

  const openBrandDetail = (match) => {
    if (!match?.brand_id) return;
    setDetailMatch(match);
  };

  return (
    <div className="p-8" data-testid="admin-deep-search-page">
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-slate-400 mb-2">
          Catalog / Bulk Lookup
        </p>
        <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight leading-none">
          Deep Search
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          Paste hundreds of brand names — separated by commas, spaces, tabs, or new lines.
          Upload a CSV/XLSX. We'll resolve each one against the full catalog, show feed partner
          + rate, and let you assign matches to a publisher in one shot.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* ─── LEFT: input panel ─── */}
        <div className="lg:col-span-1 space-y-4">
          <div className="border border-slate-200 rounded-md p-4">
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
              Brand list
            </label>
            <Textarea
              placeholder={`Glossier\nPOPFLEX, Quince\nstitchfix.com\n12834`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="min-h-[280px] font-mono text-sm resize-y"
              data-testid="deep-search-input"
            />
            <p className="text-[10px] text-slate-400 mt-1">
              {text.split(/[,\n\r;|\t]+/).filter((s) => s.trim()).length} queries detected
            </p>

            <div className="mt-4">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                Feed partner (optional)
              </label>
              <Select value={network} onValueChange={setNetwork}>
                <SelectTrigger className="h-9 text-sm" data-testid="deep-search-network-select">
                  <SelectValue placeholder="All networks" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All networks</SelectItem>
                  <SelectItem value="impact">Impact</SelectItem>
                  <SelectItem value="skimlinks">Skimlinks</SelectItem>
                  <SelectItem value="mrge">mrge</SelectItem>
                  <SelectItem value="partnerize">Partnerize</SelectItem>
                  <SelectItem value="cj">CJ</SelectItem>
                  <SelectItem value="pubadhub">PubAdHub</SelectItem>
                  <SelectItem value="pubadhub_kevin">PubAdHub 2</SelectItem>
                  <SelectItem value="admedia">AdMedia</SelectItem>
                  <SelectItem value="yieldkit">Yieldkit</SelectItem>
                  <SelectItem value="flexoffers">FlexOffers</SelectItem>
                  <SelectItem value="shopnomix_kevin">Shopnomix InMobi</SelectItem>
                  <SelectItem value="siteplug">Siteplug</SelectItem>
                  <SelectItem value="maxbounty">MaxBounty</SelectItem>
                  <SelectItem value="cashbackhive">CashbackHive</SelectItem>
                  <SelectItem value="shopnomix">Shopnomix</SelectItem>
                  <SelectItem value="pronomix">Pronomix</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400 mt-1">
                Scope the search to one feed. Yieldkit reads from <span className="font-mono">yk_advertisers</span>. Shopnomix Kevin is manual-only.
              </p>
            </div>

            <div className="mt-4">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                Publisher (optional)
              </label>
              <Select
                value={publisherId || "none"}
                onValueChange={(v) => setPublisherId(v === "none" ? "" : v)}
                onOpenChange={(o) => o && ensurePublishers()}
              >
                <SelectTrigger className="h-9 text-sm" data-testid="deep-search-publisher-select">
                  <SelectValue placeholder="Show assignment status…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— none —</SelectItem>
                  {publishers.map((p) => (
                    <SelectItem key={p.id || p._id} value={p.id || p._id}>
                      {p.email || p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400 mt-1">
                Picking a publisher enables the Assign action on each match.
              </p>
            </div>

            <div className="mt-4 flex gap-2">
              <Button
                onClick={runSearch}
                disabled={loading || !text.trim()}
                className="flex-1 bg-[#002FA7] hover:bg-[#001f6b] text-white"
                data-testid="deep-search-run"
              >
                {loading
                  ? <><CircleNotch size={14} className="mr-1.5 animate-spin" /> Searching…</>
                  : <><MagnifyingGlass size={14} className="mr-1.5" /> Search</>}
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                hidden
                accept=".csv,.xlsx,.xlsm,.tsv,.txt"
                onChange={(e) => { uploadFile(e.target.files?.[0]); e.target.value = ""; }}
                data-testid="deep-search-file-input"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                data-testid="deep-search-upload-btn"
              >
                <Upload size={14} className="mr-1.5" /> CSV / XLSX
              </Button>
            </div>
          </div>

          {/* Quick stats card */}
          {results && (
            <div className="border border-slate-200 rounded-md p-4 bg-slate-50">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Summary</div>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between"><span className="text-slate-600">Queries</span><span className="font-mono font-semibold">{results.total_queries}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Matches</span><span className="font-mono font-semibold text-emerald-700">{results.total_matches}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Not found</span><span className="font-mono font-semibold text-rose-700">{(results.unmatched || []).length}</span></div>
                {publisherId && (
                  <div className="flex justify-between pt-2 border-t border-slate-200"><span className="text-slate-600">Selected to assign</span><span className="font-mono font-semibold text-[#002FA7]">{selected.size}</span></div>
                )}
              </div>
              {publisherId && selected.size > 0 && (
                <Button
                  size="sm"
                  className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={assignSelected}
                  disabled={assigning}
                  data-testid="deep-search-assign-selected"
                >
                  {assigning
                    ? <><CircleNotch size={14} className="mr-1.5 animate-spin" /> Assigning…</>
                    : <><Plus size={14} className="mr-1.5" /> Assign {selected.size} brand{selected.size === 1 ? "" : "s"}</>}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* ─── RIGHT: results ─── */}
        <div className="lg:col-span-2">
          {!results ? (
            <div className="border border-slate-200 border-dashed rounded-md p-16 text-center text-slate-400">
              <FileText size={32} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm">Paste a list, optionally pick a publisher, then run search.</p>
              <p className="text-xs mt-1">Comma · newline · tab · pipe · semicolon — all work.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Unmatched chip cloud */}
              {(results.unmatched || []).length > 0 && (
                <div className="border border-amber-200 bg-amber-50 rounded-md p-3" data-testid="deep-search-unmatched">
                  <div className="flex items-center gap-2 mb-2">
                    <WarningCircle size={14} className="text-amber-700" weight="bold" />
                    <span className="text-xs font-bold uppercase tracking-wider text-amber-900">
                      {results.unmatched.length} not found in catalog
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {results.unmatched.slice(0, 50).map((q, i) => (
                      <span key={i} className="inline-block px-2 py-0.5 bg-white border border-amber-200 rounded text-[11px] font-mono text-amber-900">
                        {q}
                      </span>
                    ))}
                    {results.unmatched.length > 50 && (
                      <span className="text-[10px] text-amber-700 self-end">+{results.unmatched.length - 50} more…</span>
                    )}
                  </div>
                </div>
              )}

              {/* Matches table */}
              {flatMatches.length === 0 ? (
                <div className="border border-slate-200 rounded-md p-12 text-center text-sm text-slate-500">
                  No matches. Try fewer specific names or check the spelling.
                </div>
              ) : (
                <div className="border border-slate-200 rounded-md overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="deep-search-table">
                      <thead className="bg-slate-50">
                        <tr className="border-b border-slate-200">
                          {publisherId && (
                            <th className="px-3 py-2.5 w-8">
                              <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={toggleSelectAll}
                                disabled={selectableCount === 0}
                                data-testid="deep-search-select-all"
                              />
                            </th>
                          )}
                          <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Query</th>
                          <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Brand</th>
                          <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Feed</th>
                          <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Tracking URL</th>
                          <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Rate</th>
                          <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Match</th>
                          {publisherId && (
                            <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Status</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {results.results.map((group) =>
                          group.matches.map((m, i) => {
                            const isSel = selected.has(m.brand_id);
                            const ml = MATCH_LABEL[m.match_type] || MATCH_LABEL.substring;
                            return (
                              <tr
                                key={`${group.query}-${m.brand_id}-${i}`}
                                className={`border-b border-slate-100 ${isSel ? "bg-blue-50" : "hover:bg-slate-50"}`}
                              >
                                {publisherId && (
                                  <td className="px-3 py-2.5">
                                    <input
                                      type="checkbox"
                                      checked={isSel}
                                      disabled={m.assigned}
                                      onChange={() => toggleSelect(m.brand_id)}
                                      data-testid={`deep-search-checkbox-${m.brand_id}`}
                                    />
                                  </td>
                                )}
                                <td className="px-3 py-2.5 text-xs font-mono text-slate-500 truncate max-w-[150px]" title={group.query}>{group.query}</td>
                                <td className="px-3 py-2.5">
                                  <button
                                    type="button"
                                    onClick={() => openBrandDetail(m)}
                                    className="text-left group/brand"
                                    data-testid={`deep-search-brand-${m.brand_id}`}
                                  >
                                    <div className="text-sm font-medium text-[#002FA7] group-hover/brand:underline">
                                      {m.brand_name || "—"}
                                    </div>
                                    {m.advertiser_url && (
                                      <div className="text-[10px] text-slate-500 truncate max-w-[200px]">{m.advertiser_url}</div>
                                    )}
                                  </button>
                                </td>
                                <td className="px-3 py-2.5"><NetworkPill network={m.network} /></td>
                                <td className="px-3 py-2.5">
                                  <TrackingLinkCell url={m.tracking_link} />
                                </td>
                                <td className="px-3 py-2.5 text-xs font-mono text-slate-700">{m.rate_display}</td>
                                <td className="px-3 py-2.5">
                                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${ml.cls}`}>
                                    {ml.label}
                                  </span>
                                </td>
                                {publisherId && (
                                  <td className="px-3 py-2.5 text-right">
                                    {m.assigned ? (
                                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                                        <CheckCircle size={12} weight="bold" /> Assigned
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                        <X size={12} /> Not assigned
                                      </span>
                                    )}
                                  </td>
                                )}
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <BrandDetailSheet
        match={detailMatch}
        open={!!detailMatch}
        onClose={() => setDetailMatch(null)}
      />
    </div>
  );
}
